import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MultiFactorResolver } from 'firebase/auth';
import { darkTheme } from '../../lib/tokens';
import {
  signInWithEmailOrThrowMfa,
  completeTotpChallenge,
  getFreshIdToken,
  MfaRequiredError,
  signOutFirebase,
} from './firebaseAuth';
import {
  exchangeFirebaseTokenForAdminSession,
  devLoginAsAdmin,
  isAdminMfaMissingError,
} from './adminLogin';
import { useAuthStore } from './authStore';

type Step = 'credentials' | 'mfa';

interface LoginLocationState {
  from?: string;
  mfaEnrolled?: boolean;
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LoginLocationState | null) ?? null;
  const redirectTo = state?.from ?? '/';
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  const acknowledgeSessionExpired = useAuthStore((s) => s.acknowledgeSessionExpired);

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(
    state?.mfaEnrolled
      ? '2FA wurde aktiviert. Beim Login wird jetzt der Code aus deiner Authenticator-App abgefragt.'
      : null,
  );
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState(sessionExpired);
  const [submitting, setSubmitting] = useState(false);

  // Flag direkt acknowledgen, sobald die Login-Seite mountet — der Banner
  // bleibt lokal sichtbar (sessionExpiredBanner), wird aber bei einem Reload
  // oder erneuten Routenwechsel nicht erneut angezeigt.
  useEffect(() => {
    if (sessionExpired) {
      setSessionExpiredBanner(true);
      acknowledgeSessionExpired();
    }
  }, [sessionExpired, acknowledgeSessionExpired]);

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await signInWithEmailOrThrowMfa(email, password);
      // Kein MFA-Faktor → Backend lehnt mit 403 multi-factor ab.
      // Wir leiten dann zur Enrollment-Page um (Firebase-User ist signed-in).
      const idToken = await getFreshIdToken();
      const user = await exchangeFirebaseTokenForAdminSession(idToken);
      navigate(redirectTo, { replace: true });
      return user;
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        setResolver(err.resolver);
        setStep('mfa');
        setError(null);
      } else if (isAdminMfaMissingError(err)) {
        navigate('/enroll-mfa', { replace: true });
      } else {
        setError(humanError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfa(e: FormEvent) {
    e.preventDefault();
    if (!resolver) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeTotpChallenge(resolver, code);
      const idToken = await getFreshIdToken();
      await exchangeFirebaseTokenForAdminSession(idToken);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(humanError(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleBackToCredentials() {
    setError(null);
    setCode('');
    setResolver(null);
    setStep('credentials');
    await signOutFirebase().catch(() => {});
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center p-6"
      style={{
        background: `radial-gradient(120% 80% at 50% -10%, ${darkTheme.colors.accent}22 0%, transparent 55%), linear-gradient(180deg, ${'#14142A'} 0%, ${darkTheme.colors.bgBase} 100%)`,
      }}
    >
      <div
        className="w-full max-w-[420px] rounded-2xl border p-6 shadow-2xl sm:p-8"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg font-extrabold text-white"
              style={{
                background: `linear-gradient(135deg, ${darkTheme.colors.accent} 0%, ${darkTheme.colors.accentLight} 100%)`,
              }}
            >
              C
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-base font-bold text-text-primary">Currivium</span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-accent-light">
                Admin
              </span>
            </div>
          </div>
          <p className="mt-3 text-xs text-text-tertiary">Nur für autorisierten Zugang</p>
        </div>

        {sessionExpiredBanner && (
          <div
            role="status"
            className="mb-4 rounded-md p-3 text-xs"
            style={{ background: darkTheme.colors.warningBg, color: darkTheme.colors.warning }}
          >
            Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.
          </div>
        )}

        {info && (
          <div
            role="status"
            className="mb-4 rounded-md p-3 text-xs"
            style={{ background: darkTheme.colors.successBg, color: darkTheme.colors.success }}
          >
            {info}
          </div>
        )}

        {step === 'credentials' ? (
          <form onSubmit={handleCredentials} className="space-y-4">
            <h1 className="text-xl font-semibold text-text-primary">Anmelden</h1>

            <Field label="E-Mail">
              <input
                type="email"
                required
                autoFocus
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
                style={{ borderColor: darkTheme.colors.borderSubtle }}
              />
            </Field>

            <Field label="Passwort">
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
                style={{ borderColor: darkTheme.colors.borderSubtle }}
              />
            </Field>

            {error && <p className="text-sm text-danger">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md py-2.5 text-sm font-semibold text-white transition disabled:opacity-50"
              style={{ background: darkTheme.colors.accent }}
            >
              {submitting ? 'Wird angemeldet …' : 'Weiter'}
            </button>

            <p className="text-center text-xs text-text-tertiary">
              Probleme beim Login? Wende dich an Fabian.
            </p>

            {import.meta.env.DEV && (
              <div className="mt-2 border-t pt-3" style={{ borderColor: darkTheme.colors.borderSubtle }}>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={async () => {
                    setError(null);
                    setSubmitting(true);
                    try {
                      await devLoginAsAdmin();
                      navigate(redirectTo, { replace: true });
                    } catch (err) {
                      setError(humanError(err));
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  className="w-full rounded-md border border-dashed py-1.5 text-[11px] text-text-tertiary hover:bg-bg-elevated"
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                >
                  Dev-Login (überspringt 2FA)
                </button>
              </div>
            )}
          </form>
        ) : (
          <form onSubmit={handleMfa} className="space-y-4">
            <h1 className="text-xl font-semibold text-text-primary">Bestätigung mit zweitem Faktor</h1>
            <p className="text-sm text-text-secondary">
              Gib den 6-stelligen Code aus deiner Authenticator-App ein.
            </p>

            <Field label="Code">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-md border bg-bg-base px-3 py-3 text-center text-lg tabular tracking-[0.4em] text-text-primary outline-none focus:border-accent"
                style={{ borderColor: darkTheme.colors.borderSubtle }}
              />
            </Field>

            {error && <p className="text-sm text-danger">{error}</p>}

            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="w-full rounded-md py-2.5 text-sm font-semibold text-white transition disabled:opacity-50"
              style={{ background: darkTheme.colors.accent }}
            >
              {submitting ? 'Wird geprüft …' : 'Bestätigen'}
            </button>

            <button
              type="button"
              onClick={handleBackToCredentials}
              className="w-full rounded-md py-2 text-xs text-text-secondary hover:text-text-primary"
            >
              Anderes Konto verwenden
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
      {children}
    </label>
  );
}

function humanError(err: unknown): string {
  const code = (err as { code?: string }).code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-Mail oder Passwort falsch.';
    case 'auth/too-many-requests':
      return 'Zu viele Fehlversuche. Bitte später erneut.';
    case 'auth/invalid-verification-code':
      return 'Code ungültig. Erneut versuchen.';
    case 'auth/network-request-failed':
      return 'Netzwerkfehler. Bitte Verbindung prüfen.';
    default: {
      const message = (err as { message?: string }).message;
      return message ?? 'Anmeldung fehlgeschlagen.';
    }
  }
}
